import { useSetAtom, useAtom, useAtomValue } from 'jotai';
import {
  PointPickResult,
  FloorLevelStyle,
  FloorLevelProps,
  AbyssViewerContextProvider,
  Number3,
} from '@abyss/3d-viewer';
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useState,
  KeyboardEvent,
  useRef,
} from 'react';
// import { makeUniqueId } from '@apollo/client/utilities';
import { SceneCuboids } from '../../modules/cuboids';

import { TestIds } from '../../../../../cypress/testIds';

import {
  Container,
  PictureInPictureContainer,
  ParentContainer,
  ViewerLoadingWrapper,
  ViewerLoadingSpinnerContainer,
} from './styles';
import { auth0TokenState } from '@/state';

import * as state from '@/components/Analysis/state';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { FLOOR_LEVELS_BBOX_PADDING } from '@/constants';
import { Drawers } from '../Drawers';
import {
  poiState,
  usePointOfInterestMarkers,
  useBlisterSphericalClick,
  useHandlePoiClick,
} from '@/components/Analysis/modules/pointOfInterest';
import {
  usePointCloudMaterials,
  usePointCloudClick,
} from '@/components/Analysis/modules/pointCloud';
import { CuboidClickMode, Permissions, Point3d } from '@/types';

import { useMeasurementTool } from '@/components/Analysis/modules/measurementTool';
import { useHavePermission } from '@/hooks';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { AbyssViewer } from './AbyssViewer';
import { PageLoader } from '@/components/shared/PageLoader';
import { useBlisterPolygons } from '../hooks/useBlisterPolygons';
import { useImageSets } from '../hooks/useImageSets';
import {
  useAssemblyLazyQuery,
  useGetRecommendationsForAssemblyQuery,
  useUpdatePointOfInterestLocationMutation,
} from '@/__generated__/graphql';
import { useBlisterClickMarkers } from '../hooks/useBlisterClickMarkers';
// import { useFocusOnSelectedAssembly } from '../hooks/useFocusOnSelectedAssembly';
import {
  useFindViewpointsTool,
  useFindViewpointsMarker,
  useFindViewpointsFocusOnPoint,
} from '../../modules/findViewpointsTool/hooks';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import {
  cancelPOIAdd as cancelPOIAddState,
  selectedPointOfInterestId as selectedPointOfInterestIdState,
} from '../../modules/pointOfInterest/state';
import { useSavePoiScreenshot } from '../../modules/pointOfInterest/createOrEdit/useSavePoiScreenshot';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { FabricErrorBoundary } from '@/components/shared/FabricErrorBoundary';
import { CustomBlockCuboids } from '../../modules/CustomBlocks/components';
import { usePointCloudInfo } from '@/hooks/usePointCloudInfo';
import { EchoChat } from '@/components/shared/EchoChat';
import { useDrawerOpen } from '@/hooks/useDrawerOpen';
import { useAddCuboid } from '@/modules/cuboids/hooks/useAddCuboid';
import { useCuboidState } from '@/components/Analysis/modules/cuboids/hooks/useCuboidState';
import { useCuboidDataHandlers } from '@/components/Analysis/modules/cuboids/hooks/useCuboidDataHandlers';
import { useCuboidStyleHandlers } from '@/components/Analysis/modules/cuboids/hooks/useCuboidStyleHandlers';
import { usePoiDefectData } from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/hooks/usePoiDefectData';
import { PhotogrammetryTileset } from './PhotogrammetryTileset';
import { PoiEditingForms } from '../../modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/PoiEditingForms/PoiEditingForms';
import { CuboidToolbar } from '@/components/Analysis/modules/cuboids/components';
import { Box } from '@mui/material';
import { useLiveSyncAssemblyPois } from '@/components/Analysis/shared/hooks/useLiveSyncAssemblyPois';
import { usePointOfInterestAnnotations } from '../../modules/pointOfInterest/PointOfInterestTab/usePointOfInterestAnnotations';

const canvasProps = {
  style: {
    background: '#263238',
    width: '100%',
    height: '100%',
  },
  id: 'outer-viewer',
  gl: { antialias: true, preserveDrawingBuffer: true },
};

const keyCheck = (event: KeyboardEvent) => event.altKey && event.code === 'KeyA';

// const defaultSelectedColour = formatColor(DEFAULT_COLORS.selectedColour);

const floorLevelStyle: FloorLevelStyle = {
  color: 0xffffff,
  lineWidth: 0.05,
  opacity: 0.5,
};

const CreatePoiWidgetLazy = lazy(() =>
  import('@/components/Analysis/modules/pointOfInterest/CreatePoiWidget').then(
    ({ CreatePoiWidget }) => ({ default: CreatePoiWidget })
  )
);

// const CuboidWidgetLazy = lazy(() =>
//   import('@/components/Analysis/modules/cuboids/components').then(({ CuboidWidget }) => ({
//     default: CuboidWidget,
//   }))
// );

const IsolatedAssemblyLoadHelperLazy = lazy(() =>
  import('./IsolatedAssemblyLoadHelper').then(({ IsolatedAssemblyLoadHelper }) => ({
    default: IsolatedAssemblyLoadHelper,
  }))
);

const MapLocationLazy = lazy(() =>
  import('@/components/shared/MapLocation').then(({ MapLocation }) => ({
    default: MapLocation,
  }))
);

export const Inspection3dViewer = () => {
  // const globalCallbacks = useAtomValue(state.globalCallbacks);
  const recoilInspectionMetadata = useAtomValue(state.inspectionMetadata);
  const lastDeeplinkUrlParameters = useAtomValue(state.lastDeeplinkUrlParameters);

  const [selectedAssemblyId, setSelectedAssemblyId] = useAtom(state.selectedAssemblyId);
  const [selectedAssemblyName, setSelectedAssemblyName] = useAtom(state.selectedAssemblyName);
  const setSelectedAssemblyInfo = useSetAtom(state.selectedAssemblyInfo);
  const token = useAtomValue(auth0TokenState);

  const structureId = useGetStructureId();
  const structureDecks = useAtomValue(state.structureDecks);
  const areDecksVisible = useAtomValue(state.areDecksVisible);
  const selectedSpherical = useAtomValue(state.selectedSpherical);
  const viewerCurrentSpherical = useAtomValue(state.viewerCurrentSpherical);
  const viewerNextCurrentSpherical = useAtomValue(state.viewerNextCurrentSpherical);
  const setNominatedBlister = useSetAtom(state.governingValueByAssemblyId);

  const focalPointsByAssemblyId = useAtomValue(state.focalPointsByAssemblyId);
  const [cameraTarget, setCameraTarget] = useAtom(state.cameraTarget);
  const drawerLeft = useAtomValue(state.drawerLeft);
  const structureRelationships = useAtomValue(state.structureRelationship);
  // const structureLocations = useAtomValue(state.structureLocations);
  const userCanUpdateAsset = useHavePermission(Permissions.UPDATE_ASSET);
  const editPointOfInterest = useAtomValue(poiState.editPointOfInterest);
  const blisterToAdd = useAtomValue(poiState.blisterToAdd);
  const selectedPointOfInterestId = useAtomValue(selectedPointOfInterestIdState);
  const cancelPOIAdd = useSetAtom(cancelPOIAddState);
  const setAllPOI = useSetAtom(poiState.structurePois);
  const [updatePoiLocation] = useUpdatePointOfInterestLocationMutation();
  const { handleSavePoiScreenshot } = useSavePoiScreenshot();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  useLiveSyncAssemblyPois();

  const { leftDrawerWidth } = useDrawerWidths();

  const isRightDrawerOpen = useDrawerOpen('right');

  const cuboidState = useCuboidState();
  const dataHandlers = useCuboidDataHandlers();
  const styleHandlers = useCuboidStyleHandlers();

  const { setPoiDefectData, getDefectLegendOctreeKeys } = usePoiDefectData();

  const currentCuboid = useAtomValue(cuboidState.currentCuboid);
  const cuboidClickMode = useAtomValue(cuboidState.cuboidClickMode);

  const showPointCloud = useAtomValue(state.showPointCloud);

  const addCuboid = useAddCuboid(cuboidState, dataHandlers, styleHandlers);
  const isCuboidEditorEnabled = useAtomValue(cuboidState.isCuboidEditorEnabled);

  const nodesPerAssemblyPath = useMemo(
    () => recoilInspectionMetadata?.nodesPerAssemblyPath,
    [recoilInspectionMetadata]
  );

  const [nodesToShow, setNodesToShow] = useState<Set<string> | undefined>(undefined);

  const [assemblyLazyQuery] = useAssemblyLazyQuery();

  const isolatedAssemblyNodesUrl = useMemo<string | undefined>(() => {
    if (!structureRelationships || !nodesPerAssemblyPath) {
      return undefined;
    }
    if (!selectedAssemblyId || selectedSpherical) {
      return undefined;
    }
    if (!structureRelationships.assemblyIdToAssembly3d) {
      return undefined;
    }
    const assembly3d = structureRelationships.assemblyIdToAssembly3d[selectedAssemblyId];
    if (assembly3d === undefined || assembly3d === null) {
      return undefined;
    }
    return getCloudfrontUrl(`${nodesPerAssemblyPath}/${assembly3d}.json`);
  }, [structureRelationships, selectedAssemblyId, selectedSpherical, nodesPerAssemblyPath]);

  useEffect(() => {
    if (!isolatedAssemblyNodesUrl) {
      setNodesToShow(undefined);
    }
  }, [isolatedAssemblyNodesUrl]);

  const { maxIds, onOctreeHierarchyLoaded } = usePointCloudInfo();

  const { material, pointCloudMaterials } = usePointCloudMaterials(maxIds);

  const { data: assemblyRecommendation } = useGetRecommendationsForAssemblyQuery({
    variables: {
      assemblyId: selectedAssemblyId!,
    },
    fetchPolicy: 'network-only',
    skip: !selectedAssemblyId,
  });
  // Temporary workaround for slow loading of bin and image files in Pakistan
  // 1. Get the point cloud and image files
  //     - Download horn-mountain-pd.zip from https://drive.google.com/drive/folders/13hdwkiJqbS0prPZi0WtW21av0UcHTm7h?usp=sharing
  //     - Extract to a folder e.g. C:/ - you should see 2 folders inside "horn-mountain-pd" - "images" with jpg files and "octree" with bin files
  //     - Rename the containing folder to 6298b4cf34349ba5acb519d4
  // 2. Host these files on a server within your own machine
  //     - Download and install LTS version of npm releavant to your OS https://nodejs.org/en/download/
  //     - From a command line run: npm install --global http-server
  //     - From a command line, navigate to the folder horn-mountain-pd
  //     - From within horn-mountain-pd in command line run: http-server --cors='*'
  //     - You should see some output saying Available on: http://127.0.0.1:8080
  // 3. Enable Fabric V2 to load files from your own machine
  //     - Go to Fabric V2 temporary version here, stay on Platforms list page for now - https://d2gplkryt7cldz.cloudfront.net/
  //     - Open Chrome dev tools (usually Ctrl+Shift+i)
  //     - Open Console tab (usually second from the left after Elements)
  //     - Run the following in Console tab (paste this in then hit Enter): localStorage.setItem("files-base-url", "http://localhost:8080")
  //     - Select Horn Mountain PD platform - this should now load point cloud and image files from your local machine

  // To use a http server instead of local machine
  // 1. Set up file/folder structure on http server as follows
  // /6298b4cf34349ba5acb519d4 - horn mountain PD
  //    /images - should contain all the .jpg files for horn mountain PD sphericals
  //    /octree - should contain octree_hierarch.json and .bin files for horn mountain PD
  // /61e7ca258f1a3cc4411029cc - marlin UD - lower priority, once horn mountain (folder above) is working end to end
  //    /images - should contain all the .jpg files for marlin UD sphericals
  //    /octree - should contain octree_hierarch.json and .bin files for marlin UD

  // 2. Enable Fabric V2 to load files from your own machine - this only works for Horn Mountain PD
  //     - Go to Fabric V2 temporary version here and log in, stay on Platforms list page for now - https://d2gplkryt7cldz.cloudfront.net/
  //     - Open Chrome dev tools (usually Ctrl+Shift+i)
  //     - Open Console tab (usually second from the left after Elements)
  //     - Run the following in Console tab and replace http://localhost:8080 with your http server url (paste this in then hit Enter): localStorage.setItem("files-base-url", "http://localhost:8080")
  //     - Select any platform - this should now load point cloud and image files from your local machine

  const octreePointCloudsJsonPath = useMemo(() => {
    if (!showPointCloud) return [];
    const filesBaseUrl = localStorage.getItem('files-base-url');
    if (filesBaseUrl) {
      return [
        {
          path: `${filesBaseUrl}/${structureId}/octree/octree_hierarchy.json`,
          nodesToShow,
          contents: [{ 'part-id': true }, { 'corrosion-id': true }, { 'rusting-id': true }],
          onOctreeHierarchyLoaded,
        },
      ];
    }
    if (recoilInspectionMetadata?.octreeResourcesPath) {
      return [
        {
          path: getCloudfrontUrl(recoilInspectionMetadata.octreeResourcesPath),
          nodesToShow,
          contents: [{ 'part-id': true }, { 'corrosion-id': true }, { 'rusting-id': true }],
          onOctreeHierarchyLoaded,
        },
      ];
    }
    return [];
  }, [structureId, recoilInspectionMetadata, nodesToShow, onOctreeHierarchyLoaded, showPointCloud]);

  const { sphericalImages, rectilinearImages } = useImageSets(structureId);

  const previousFocalPoint = useRef<
    | {
        selectedAssemblyId: string | undefined;
        focalPointEntry:
          | {
              focalPoint?: Point3d;
              cameraPose?: {
                position: Point3d;
                lookAt: Point3d;
              };
            }
          | undefined;
      }
    | undefined
  >(undefined);

  /**
   * Sets camera target to look at the entire selected assembly in 3d view mode
   */
  useEffect(() => {
    if (!lastDeeplinkUrlParameters) return;
    if (
      lastDeeplinkUrlParameters.lookAt &&
      performance.now() < lastDeeplinkUrlParameters.time + 5000
    ) {
      return;
    }

    const focalPointEntry = selectedAssemblyName
      ? focalPointsByAssemblyId[selectedAssemblyName]
      : undefined;

    const oldState = previousFocalPoint.current;
    previousFocalPoint.current = {
      selectedAssemblyId,
      focalPointEntry,
    };

    // Not changing camera position, when exiting spherical view mode
    if (
      oldState?.selectedAssemblyId === selectedAssemblyId &&
      oldState?.focalPointEntry === focalPointEntry
    )
      return;

    if (!selectedAssemblyName) return;
    if (!selectedAssemblyId) return;
    if (cameraTarget) return;
    if (viewerCurrentSpherical || viewerNextCurrentSpherical) return;

    if (!focalPointEntry) return;
    const { focalPoint, cameraPose } = focalPointEntry;
    if (cameraPose) {
      const { position, lookAt } = cameraPose;
      const { x, y, z } = position;
      const { x: lx, y: ly, z: lz } = lookAt;
      setCameraTarget({
        position: [x, y, z],
        lookAt: [lx, ly, lz],
      });
    } else if (focalPoint) {
      const { x, y, z } = focalPoint;
      setCameraTarget({
        position: [x, y + 15, z + 5],
        lookAt: [x, y, z],
      });
    }
  }, [
    lastDeeplinkUrlParameters,
    cameraTarget,
    focalPointsByAssemblyId,
    selectedAssemblyId,
    selectedAssemblyName,
    viewerCurrentSpherical,
    viewerNextCurrentSpherical,
    setCameraTarget,
  ]);

  useEffect(() => {
    const governingValueForAssembly =
      assemblyRecommendation?.assembly?.recommendation?.governingValue;

    if (governingValueForAssembly && selectedAssemblyId) {
      setNominatedBlister((current) => {
        return {
          ...current,
          [selectedAssemblyId]: governingValueForAssembly,
        };
      });
    }
  }, [
    assemblyRecommendation?.assembly?.recommendation?.governingValue,
    selectedAssemblyId,
    setNominatedBlister,
  ]);
  // const assemblyAnnotation3dArray = useMemo(() => {
  //   if (!selectedAssemblyId) return undefined;
  //   if (!selectedSpherical) return undefined;
  //   if (!structureRelationships?.assemblyIdAndLocationIdToAnnotation3dArray) return undefined;

  //   const locationId = structureLocations?.find(
  //     (location) => location.name === selectedSpherical.name
  //   )?.id;
  //   if (!locationId) return undefined;

  //   const assemblyEntry =
  //     structureRelationships?.assemblyIdAndLocationIdToAnnotation3dArray[selectedAssemblyId];

  //   return assemblyEntry && Object.hasOwn(assemblyEntry, locationId)
  //     ? assemblyEntry[locationId]
  //     : [];
  // }, [selectedAssemblyId, selectedSpherical, structureRelationships, structureLocations]);

  // useFocusOnSelectedAssembly(
  //   selectedAssemblyId,
  //   assemblyAnnotation3dArray,
  //   selectedSpherical,
  //   globalCallbacks,
  //   setCameraTarget
  // );

  const toggleSelectedAssembly = useCallback(
    (assemblyId?: string) => {
      if (!assemblyId) return;

      setSelectedAssemblyInfo((previousSelectedAssemblyInfo) => {
        if (previousSelectedAssemblyInfo === assemblyId) {
          return undefined;
        }

        return assemblyId;
      });
    },
    [setSelectedAssemblyInfo]
  );

  const handlePOIClick = useHandlePoiClick();
  const { annotation3dToAssemblyId } = usePointCloudClick();
  const { measurements, isMeasurementMenuVisible, handleMeasurementsOnPointSelect } =
    useMeasurementTool();

  const { isFindViewpointsToolActive, onPointSelect: handleFindLocationsOnPointSelect } =
    useFindViewpointsTool();

  const isCuboidToolActive = !currentCuboid && cuboidClickMode === CuboidClickMode.AddCuboid;

  const handleUpdatePoiLocation = useCallback(
    async (position: Number3) => {
      if (!selectedPointOfInterestId) {
        showSnackBar('No Point of Interest selected. Contact Support', 'error');
        return;
      }
      const [x, y, z] = position;

      try {
        const { data } = await updatePoiLocation({
          variables: {
            input: {
              pointOfInterestId: selectedPointOfInterestId,
              point: { x, y, z },
              locationId: viewerCurrentSpherical?.id,
            },
          },
        });
        const updatedPoi = data?.updatePointOfInterestLocation;
        if (updatedPoi) {
          setAllPOI((previous) =>
            previous.map((poi) => (poi.id === updatedPoi.id ? updatedPoi : poi))
          );
          showSnackBar('Point of Interest location updated successfully', 'success');
          await handleSavePoiScreenshot({ poiId: updatedPoi.id, refetchPoi: true });
        }
      } catch {
        showSnackBar('Something went wrong while updating location. Please try again', 'error');
      }
    },
    [
      handleSavePoiScreenshot,
      selectedPointOfInterestId,
      setAllPOI,
      showSnackBar,
      updatePoiLocation,
      viewerCurrentSpherical?.id,
    ]
  );

  const handleToolsPointClick = useCallback(
    (position: Number3): boolean => {
      if (isCuboidToolActive) {
        addCuboid({ position });
        return false;
      }

      if (isMeasurementMenuVisible) {
        handleMeasurementsOnPointSelect(position);
        return true;
      }
      if (isFindViewpointsToolActive) {
        handleFindLocationsOnPointSelect(position);
        return true;
      }
      return false;
    },
    [
      addCuboid,
      handleMeasurementsOnPointSelect,
      handleFindLocationsOnPointSelect,
      isCuboidToolActive,
      isMeasurementMenuVisible,
      isFindViewpointsToolActive,
    ]
  );

  const handleEditPOIPointClick = useCallback(
    (position: Number3, assemblyId: string | undefined): boolean => {
      if (editPointOfInterest.state === 'Inactive' || assemblyId !== selectedAssemblyId) {
        return false;
      }
      if (editPointOfInterest.formState === 'Update-Location') {
        cancelPOIAdd();
        handleUpdatePoiLocation(position);
        return true;
      }

      if (handlePOIClick(position)) {
        return true;
      }

      return false;
    },
    [cancelPOIAdd, handleUpdatePoiLocation, handlePOIClick, editPointOfInterest, selectedAssemblyId]
  );

  const onPointCloudClick = useCallback(
    (pickResult: PointPickResult) => {
      const { pointIndex, getPointAttribute, getPointAttributes } = pickResult;

      const rawPosition = getPointAttribute(pointIndex, 'position');
      if (!Array.isArray(rawPosition) || rawPosition.length !== 3) return;
      const position = rawPosition as Number3;

      if (handleToolsPointClick(position)) {
        return;
      }

      const defectLegendOctreeKeys = getDefectLegendOctreeKeys();

      if (defectLegendOctreeKeys.length > 0) {
        const defectsAttributes = getPointAttributes(pointIndex, defectLegendOctreeKeys);
        setPoiDefectData(defectsAttributes);
      }

      const annotation3dReference = getPointAttribute(pointIndex, 'annotation');
      if (typeof annotation3dReference !== 'number') return;
      const assemblyId = annotation3dToAssemblyId(annotation3dReference);

      if (handleEditPOIPointClick(position, assemblyId)) {
        return;
      }

      if (!selectedSpherical) toggleSelectedAssembly(assemblyId);

      const fetchAssemblyName = async () => {
        try {
          const { data } = await assemblyLazyQuery({
            variables: {
              assemblyId,
            },
          });

          setSelectedAssemblyName(data?.assembly?.tagName);
        } catch {
          setSelectedAssemblyName(undefined);
        }
      };

      if (assemblyId !== selectedAssemblyId && selectedSpherical) {
        setSelectedAssemblyId(assemblyId);
        fetchAssemblyName();
      }
    },
    [
      handleToolsPointClick,
      handleEditPOIPointClick,
      annotation3dToAssemblyId,
      selectedAssemblyId,
      selectedSpherical,
      toggleSelectedAssembly,
      setSelectedAssemblyId,
      assemblyLazyQuery,
      setSelectedAssemblyName,
      getDefectLegendOctreeKeys,
      setPoiDefectData,
    ]
  );

  const onModelClick = useCallback(
    (point: Number3) => {
      if (handleToolsPointClick(point)) {
        return;
      }
      if (selectedAssemblyId && handleEditPOIPointClick(point, selectedAssemblyId)) {
        return;
      }
    },
    [handleToolsPointClick, handleEditPOIPointClick, selectedAssemblyId]
  );

  const floorLevels = useMemo(() => {
    if (structureDecks && areDecksVisible) {
      const mappedFloorLevels: FloorLevelProps[] = structureDecks.map(
        ({ name, cadFloorHeight }) => ({
          name,
          label: {
            color: '#888888',
            fontSize: 16,
          },
          labelOffset: [1, 1, 1],
          zLevel: cadFloorHeight ?? 0,
          style: { ...floorLevelStyle },
          gridCellSize: 8,
          bboxPadding: FLOOR_LEVELS_BBOX_PADDING,
        })
      );
      return mappedFloorLevels;
    }
    return [];
  }, [structureDecks, areDecksVisible]);

  const poiMarkerSets = usePointOfInterestMarkers(selectedSpherical);
  const poiAnnotationSets = usePointOfInterestAnnotations();
  const blisterPolygons = useBlisterPolygons();
  const blisterMarkers = useBlisterClickMarkers();
  const handleBlisterSphericalClick = useBlisterSphericalClick();
  const [assemblyLoading, setAssemblyLoading] = useState(false);
  const navbarHeight = useNavbarHeight();

  const findViewpointsMarker = useFindViewpointsMarker();
  useFindViewpointsFocusOnPoint();

  const markers = useMemo(() => {
    const actualBlisterMarkers = selectedSpherical && blisterMarkers ? blisterMarkers : [];
    return findViewpointsMarker
      ? [...actualBlisterMarkers, findViewpointsMarker]
      : actualBlisterMarkers;
  }, [selectedSpherical, blisterMarkers, findViewpointsMarker]);

  const [displayAnimationEditor, setDisplayAnimationEditor] = useAtom(state.displayAnimationEditor);

  const handleKeyPress = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (keyCheck(event)) {
        setDisplayAnimationEditor((oldValue) => !oldValue);
      }
    },
    [setDisplayAnimationEditor]
  );

  const outerDivStyle: React.CSSProperties = useMemo(
    () => ({
      width: '100%',
      height: '100%',
      position: 'relative',
      ...((blisterToAdd && blisterToAdd.state === 'WaitingForCenter') ||
      (blisterToAdd && blisterToAdd.state === 'WaitingForEdge') ||
      (editPointOfInterest && editPointOfInterest.state === 'WaitingForPoint')
        ? { cursor: 'crosshair' }
        : {}),
    }),
    [blisterToAdd, editPointOfInterest]
  );

  const createPoiComponent = useMemo(() => {
    return <PoiEditingForms open={editPointOfInterest.state === 'WaitingForDetail'} />;
  }, [editPointOfInterest.state]);

  return (
    <>
      {!recoilInspectionMetadata && (
        <ViewerLoadingWrapper>
          <ViewerLoadingSpinnerContainer navbarHeight={navbarHeight}>
            <PageLoader background="dark" />
          </ViewerLoadingSpinnerContainer>
        </ViewerLoadingWrapper>
      )}

      {recoilInspectionMetadata && token && (
        <ParentContainer navbarHeight={navbarHeight}>
          <Container data-test-id={TestIds.PLATFORM_EXPLORER_INSIGHTS}>
            <EchoChat isRightDrawerOpen={isRightDrawerOpen} />
            <Drawers onAssemblyLoading={setAssemblyLoading} />
            {editPointOfInterest?.pointOfInterest && createPoiComponent}
            {isCuboidEditorEnabled && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 20,
                  width: 'auto',
                  right: 0,
                  marginRight: `${leftDrawerWidth + 60}px`,
                  zIndex: 102,
                }}
              >
                <CuboidToolbar />
              </Box>
            )}
            <div style={outerDivStyle} tabIndex={1} onKeyPress={handleKeyPress}>
              <FabricErrorBoundary logToSentry>
                <AbyssViewerContextProvider canvasProps={canvasProps}>
                  <AbyssViewer
                    assemblyLoading={assemblyLoading}
                    octreePointCloudsJsonPath={octreePointCloudsJsonPath}
                    pointCloudMaterials={pointCloudMaterials}
                    sphericalImages={sphericalImages}
                    rectilinearImages={rectilinearImages}
                    onSphericalViewClick={handleBlisterSphericalClick}
                    svgMarkerSets={poiMarkerSets}
                    floorLevels={floorLevels}
                    measurementLines={measurements}
                    material={material}
                    onPointCloudClick={onPointCloudClick}
                    onModelClick={onModelClick}
                    polygons={blisterPolygons}
                    markers={markers}
                    displayAnimationEditor={displayAnimationEditor}
                    htmlMarkerSets={poiAnnotationSets}
                  >
                    <SceneCuboids />
                    <CustomBlockCuboids />
                    <PhotogrammetryTileset onClick={onModelClick} />
                  </AbyssViewer>

                  {isolatedAssemblyNodesUrl && (
                    <Suspense>
                      <IsolatedAssemblyLoadHelperLazy
                        assemblyNodesUrl={isolatedAssemblyNodesUrl}
                        setNodesToShow={setNodesToShow}
                      />
                    </Suspense>
                  )}
                </AbyssViewerContextProvider>
              </FabricErrorBoundary>
            </div>
            {selectedSpherical && (
              <PictureInPictureContainer
                openState={drawerLeft?.visibility}
                horizontalOffset={leftDrawerWidth}
              >
                <Suspense>
                  <MapLocationLazy />
                </Suspense>
              </PictureInPictureContainer>
            )}
            {selectedAssemblyId && userCanUpdateAsset && (
              <Suspense>
                <CreatePoiWidgetLazy />
              </Suspense>
            )}
          </Container>
        </ParentContainer>
      )}
    </>
  );
};
